import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import { iziplayBooks } from '../tools/api'
import { queryKeys } from './keys'

/*
 * Shapes from services/books (ReadingDetailsModel, KoboDeviceModel,
 * AvailableBooksModel).
 */

// The iziplay services answer RFC 7807 problems; `detail` is the human line.
export function problemDetail(err: unknown, fallback: string): string {
  if (isAxiosError(err)) return err.response?.data?.detail ?? fallback
  return fallback
}

export type BookStatus = 'NOT_STARTED' | 'DOWNLOADING' | 'DOWNLOADED'

export interface Book {
  title: string
  author: string
  publisher?: string
  year?: number
  description?: string
}

export interface Reading {
  id: string
  createdAt: string
  isbn: string
  progress: number
}

export interface ReadingDetails {
  reading: Reading
  book?: Book
  status: BookStatus
  url?: string
}

export interface KoboDevice {
  id: string
  createdAt: string
  nickname: string
  model?: string
  externalDeviceId?: string
  appVersion?: string
  osVersion?: string
  serialNumber?: string
}

/*
 * The catalog: services/books' GET /v1/downloads, the books Anna already
 * has on disk, each with a per-user download URL.
 */

export interface AvailableBookIdentifier {
  type: string
  value: string
}

export interface AvailableBook {
  id: string
  title: string
  author: string
  publisher: string
  year: number
  coverURL: string
  description?: string
  languages: string[]
  identifiers: AvailableBookIdentifier[]
  url?: string
}

export interface AvailableBooks {
  total: number
  results: AvailableBook[]
}

// Covers are public on the books service, so a plain img src works.
export const bookCoverURL = (isbn: string) =>
  `${import.meta.env.REACT_APP_IZIPLAY_URL_BOOKS}/v1/covers/${isbn}`

// The same endpoint keyed by Anna record id instead of ISBN: the service
// extracts the cover from the EPUB it has on the server, and falls back to
// the ISBN flow when it has none.
export const bookCoverByAnnaIDURL = (annaID: string) =>
  `${import.meta.env.REACT_APP_IZIPLAY_URL_BOOKS}/v1/covers/anna/${encodeURIComponent(annaID)}`

// The URL a Kobo e-reader's api_endpoint is pointed at; the device id is
// the whole of authentication.
export const koboEndpointURL = (deviceId: string) =>
  `${import.meta.env.REACT_APP_IZIPLAY_URL_KOBO}/kobo/${deviceId}`

export function useReadings() {
  return useQuery({
    queryKey: queryKeys.readings,
    queryFn: async (): Promise<ReadingDetails[]> => {
      const res = await iziplayBooks().get<ReadingDetails[]>('/v1/readings')
      return res.data
    },
    // Poll while Anna is still fetching a book, so the card flips to
    // "Ready" without a reload.
    refetchInterval: query =>
      query.state.data?.some(r => r.status === 'DOWNLOADING') ? 10_000 : false
  })
}

export function useAddReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (isbn: string) => iziplayBooks().post('/v1/readings', { isbn }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.readings })
  })
}

export function useDeleteReading() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (readingId: string) => iziplayBooks().delete(`/v1/readings/${readingId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.readings })
  })
}

export function useAvailableBooks() {
  return useQuery({
    queryKey: queryKeys.availableBooks,
    queryFn: async (): Promise<AvailableBooks> => {
      const res = await iziplayBooks().get<AvailableBooks>('/v1/downloads')
      return res.data
    }
  })
}

export function useKoboDevices() {
  return useQuery({
    queryKey: queryKeys.koboDevices,
    queryFn: async (): Promise<KoboDevice[]> => {
      const res = await iziplayBooks().get<KoboDevice[]>('/v1/devices/kobo')
      return res.data
    }
  })
}

export function useAddKoboDevice() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (nickname: string): Promise<KoboDevice> => {
      const res = await iziplayBooks().post<KoboDevice>('/v1/devices/kobo', { nickname })
      return res.data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.koboDevices })
  })
}
